/**
 * @file ShopCarriersPage.tsx
 * @description Раздел «Доставка → Транспортные компании» в кабинете магазина
 * (phase10.5.Y Carrier Management Module).
 *
 * Список карточек ТК. Для каждой:
 *  - Если ТК настроена → Switch isActive + стата + кнопка «Настроить».
 *  - Если ТК подключена на платформе (есть Integration), но не настроена → «Подключить к магазину».
 *  - Если на платформе тоже не подключена → ссылка-подсказка.
 */

import { Loader2, Settings, Truck, AlertTriangle, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DeliveryControlCard } from "../../../components/carriers/DeliveryControlCard";
import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Switch } from "../../../components/ui/switch";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  listShopCarriers,
  removeShopCarrier,
  upsertShopCarrier,
  type ShopCarrierConfigDto,
} from "../../../lib/api-shop-carriers";

export function ShopCarriersPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { slug } = useParams();
  // phase19.12 — настройки открываются страницей, а не модалкой.
  const basePath = `/shop/${slug}/carriers`;
  const [items, setItems] = useState<ShopCarrierConfigDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyCarrier, setBusyCarrier] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const data = await listShopCarriers();
      setItems(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function replaceItem(updated: ShopCarrierConfigDto): void {
    setItems((prev) => prev.map((it) => (it.carrier === updated.carrier ? updated : it)));
  }

  async function onToggleActive(carrier: ShopCarrierConfigDto, next: boolean): Promise<void> {
    setBusyCarrier(carrier.carrier);
    try {
      const updated = await upsertShopCarrier(carrier.carrier, { isActive: next });
      replaceItem(updated);
      notify.success(next ? "ТК активирована" : "ТК отключена");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось изменить статус");
    } finally {
      setBusyCarrier(null);
    }
  }

  async function onConnectToShop(carrier: ShopCarrierConfigDto): Promise<void> {
    setBusyCarrier(carrier.carrier);
    try {
      const updated = await upsertShopCarrier(carrier.carrier, {
        isActive: true,
        priority: 100,
        cashOnDelivery: false,
        onlinePayment: true,
      });
      replaceItem(updated);
      notify.success(`${carrier.carrierLabel} подключена к магазину`);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось подключить");
    } finally {
      setBusyCarrier(null);
    }
  }

  async function onDisconnect(carrier: ShopCarrierConfigDto): Promise<void> {
    if (
      !window.confirm(
        `Отключить ${carrier.carrierLabel} от магазина? Все настройки сбросятся, активные отгрузки сохранятся.`,
      )
    ) {
      return;
    }
    setBusyCarrier(carrier.carrier);
    try {
      await removeShopCarrier(carrier.carrier);
      await refresh();
      notify.success(`${carrier.carrierLabel} отключена`);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось отключить");
    } finally {
      setBusyCarrier(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Доставка · Транспортные компании</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Транспортные компании</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Подключите ТК к магазину, настройте параметры (вес, COD, города) и
          синхронизируйте пункты выдачи. Credentials API настраиваются
          платформенным администратором в разделе «Интеграции».
        </p>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => (
            <CarrierCard
              key={c.carrier}
              carrier={c}
              busy={busyCarrier === c.carrier}
              onToggleActive={(v) => onToggleActive(c, v)}
              onConnect={() => onConnectToShop(c)}
              onConfigure={() => navigate(`${basePath}/${c.carrier.toLowerCase()}/settings`)}
              onDisconnect={() => onDisconnect(c)}
            />
          ))}
          {items.length === 0 ? (
            <EditorialCard>
              <EditorialCard.Body className="text-center py-12 text-on-surface-variant">
                Транспортных компаний пока нет.
              </EditorialCard.Body>
            </EditorialCard>
          ) : null}
        </div>
      )}

      {/* phase19.13 шаг 5 — пороги списка «Требуют внимания». */}
      <DeliveryControlCard />
    </div>
  );
}

interface CarrierCardProps {
  carrier: ShopCarrierConfigDto;
  busy: boolean;
  onToggleActive: (next: boolean) => void;
  onConnect: () => void;
  onConfigure: () => void;
  onDisconnect: () => void;
}

function CarrierCard({
  carrier,
  busy,
  onToggleActive,
  onConnect,
  onConfigure,
  onDisconnect,
}: CarrierCardProps) {
  return (
    <EditorialCard floating>
      <EditorialCard.Body className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
              <Truck className="size-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                {carrier.carrierLabel}
              </h2>
              <p className="text-xs text-on-surface-variant">{carrier.carrier}</p>
            </div>
          </div>
          <StatusBadge carrier={carrier} />
        </div>

        {/* Состояние подключения */}
        {!carrier.integrationConfigured ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex gap-2 items-start">
            <AlertTriangle className="size-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p>
                Не подключена на платформе. Передайте платформенному
                администратору, чтобы он подключил{" "}
                <strong>{carrier.carrierLabel}</strong> в разделе «Интеграции».
              </p>
              <Link
                to="/platform/integrations"
                className="text-primary underline inline-flex items-center gap-1 mt-1 text-xs"
              >
                Открыть платформенные интеграции
                <ExternalLink className="size-3" />
              </Link>
            </div>
          </div>
        ) : !carrier.configured ? (
          <div className="rounded-md border border-on-surface/10 bg-surface-low p-3 text-sm">
            <p className="text-on-surface-variant">
              ТК подключена на платформе. Подключите её к магазину, чтобы
              включить в выбор способов доставки.
            </p>
            <Button
              size="sm"
              className="mt-2"
              onClick={onConnect}
              disabled={busy}
            >
              {busy ? (
                <Loader2 className="size-4 mr-1.5 animate-spin" />
              ) : null}
              Подключить к магазину
            </Button>
          </div>
        ) : (
          <>
            {/* Статистика */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Отгрузок / 30 дн." value={carrier.stats.totalLast30d} />
              <Stat label="Доставлено" value={carrier.stats.delivered} />
              <Stat
                label="% доставки"
                value={`${carrier.stats.deliveryRate}%`}
                accent={carrier.stats.deliveryRate >= 90}
              />
            </div>

            {/* Управление. На 360px кнопки не помещались в один ряд с
                переключателем и уезжали за край карточки (phase19.12) →
                переключатель и кнопки переносятся, кнопки тянутся по ширине. */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <div className="flex items-center gap-2">
                <Switch
                  checked={carrier.isActive}
                  onCheckedChange={(v) => onToggleActive(Boolean(v))}
                  disabled={busy}
                />
                <span className="text-sm">
                  {carrier.isActive ? "Активна" : "Отключена"}
                </span>
              </div>
              <div className="flex flex-1 items-center gap-2 sm:flex-none">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onConfigure}
                  className="flex-1 sm:flex-none"
                >
                  <Settings className="size-4 mr-1.5" />
                  Настроить
                </Button>
                <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy}>
                  Отключить
                </Button>
              </div>
            </div>
          </>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5 p-2 rounded-md bg-surface-low">
      <span
        className={[
          "text-xl font-semibold tracking-tight",
          accent ? "text-primary" : "text-on-surface",
        ].join(" ")}
      >
        {value}
      </span>
      <span className="text-[11px] text-on-surface-variant text-center">{label}</span>
    </div>
  );
}

function StatusBadge({ carrier }: { carrier: ShopCarrierConfigDto }) {
  if (!carrier.integrationConfigured) {
    return <Badge variant="outline">Не подключена</Badge>;
  }
  if (!carrier.configured) {
    return <Badge variant="secondary">Не настроена</Badge>;
  }
  return carrier.isActive ? (
    <Badge>Активна</Badge>
  ) : (
    <Badge variant="outline">Отключена</Badge>
  );
}
